'use client';

import { useRouter } from '@bprogress/next/app';
import { useSelector } from '@xstate/store/react';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

import { GTMEcommerceItem, sendGTMEventEcommerce } from '@/fo/api/gtm';
import { ProductStockStatus } from '@/fo/api/magento';
import {
  storeProduct,
  selectCountProductInWishlist,
  cartActions,
  wishlistActions,
  selectWishlistItemId,
  selectCartItemId,
  selectCountProductInCart
} from '@/fo/store/product';
import { TAPBAR_MOBILE_ID } from '@/fo/ui/app/tapbar/mobile';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { useMediaQuery } from '@/fo/ui/mod/media/query';
import { UiModPortalBase } from '@/fo/ui/mod/portal/base';
import { UiProductCounterInputBase } from '@/fo/ui/product/counter-input/base';
import {
  EVENT_NAME_PRODUCT_INFO_MAIN_HIDE,
  EVENT_NAME_PRODUCT_INFO_MAIN_SHOW
} from '@/fo/ui/product/info/main';
import { UiProductWishlistButton } from '@/fo/ui/product/wishlist/button';
import { utilEventAuthModal } from '@/fo/util/event-auth-modal';
import { utilEventConfigurableProduct } from '@/fo/util/event-configurable-product';
import { utilEventElementVisibility } from '@/fo/util/event-element-visibility';
import { utilEventProductSubscribeModal } from '@/fo/util/event-product-subscribe-modal';
import { MappedProduct } from '@/fo/util/map-product-data';

import { styles } from './FeatureProductHeaderMobile.styles';

export interface FeatureProductHeaderMobileProps {
  product: MappedProduct;
  isAuthorized: boolean;
}

export function FeatureProductHeaderMobile({
  product: initialProduct,
  isAuthorized
}: FeatureProductHeaderMobileProps) {
  const slots = styles();
  const t = useTranslations();
  const router = useRouter();

  const isMobile = useMediaQuery('lg', 'max');

  const [product, setProduct] = useState<MappedProduct>(
    initialProduct.configurableVariants?.find((item) => item.isSelected)
      ?.product || initialProduct
  );
  const [hasTapbarContainer, setHasTapbarContainer] = useState(false);
  const [isHidden, setIsHidden] = useState(true);
  const cartCount = useSelector(
    storeProduct,
    selectCountProductInCart(product.sku)
  );
  const cartItemId = useSelector(storeProduct, selectCartItemId(product.sku));
  const wishlistCount = useSelector(
    storeProduct,
    selectCountProductInWishlist(product.sku)
  );
  const wishlistItemId = useSelector(
    storeProduct,
    selectWishlistItemId(product.sku)
  );
  const itemGtmData: GTMEcommerceItem = {
    item_id: product.sku,
    item_name: product.name,
    price: String(product.finalPrice ?? product.basePrice ?? ''),
    item_brand: product.brand || '',
    item_category: product.series || product.brand || '',
    variant: product.volume ?? '',
    item_list_name: 'product-detail-page',
    position: 1,
    quantity: 1
  };

  useEffect(() => {
    const unsubscribeOnOpen = utilEventConfigurableProduct.onOpen((product) => {
      setProduct(product);
    });

    const unsubscribeOnProductSelect =
      utilEventConfigurableProduct.onProductSelect((product) => {
        setProduct(product);
      });

    const checkContainer = () => {
      const container = document.getElementById(TAPBAR_MOBILE_ID);
      setHasTapbarContainer(!!container);
    };

    checkContainer();

    const observer = new MutationObserver(checkContainer);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      unsubscribeOnOpen();
      unsubscribeOnProductSelect();
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const unsubscribeOnShow = utilEventElementVisibility.onShow(
      EVENT_NAME_PRODUCT_INFO_MAIN_SHOW,
      () => {
        setIsHidden(false);
      }
    );

    const unsubscribeOnHide = utilEventElementVisibility.onHide(
      EVENT_NAME_PRODUCT_INFO_MAIN_HIDE,
      () => {
        setIsHidden(true);
      }
    );

    return () => {
      unsubscribeOnShow();
      unsubscribeOnHide();
    };
  }, []);

  const isInWishlist = wishlistCount > 0;
  const isInCart = cartCount > 0;

  const onCartChange = async (quantity: number) => {
    if (!cartItemId) return;

    if (quantity === 0) {
      await cartActions.removeCartItem(product.sku, cartItemId, product);
    } else {
      await cartActions.updateCartItem(
        product.sku,
        cartItemId,
        quantity,
        product
      );
    }
  };

  const onWishlistClick = () => {
    if (isAuthorized) {
      if (isInWishlist && wishlistItemId) {
        wishlistActions.removeWishlistItems([
          { sku: product.sku, wishlistItemId }
        ]);
      } else {
        sendGTMEventEcommerce({
          event: 'add_to_wishlist',
          ecommerce: {
            value: itemGtmData.price,
            items: [itemGtmData]
          }
        });
        wishlistActions.addProductToWishlist(product.sku, 1);
      }
    } else {
      utilEventAuthModal.open('wishlist-button');
    }
  };

  const onCartClick = () => {
    if (product.stockStatus === ProductStockStatus.OutOfStock) {
      utilEventProductSubscribeModal.open(product.mindboxId);
      return;
    }

    if (isInCart) {
      router.push('/cart');
    } else {
      cartActions.addProductsToCart([
        {
          sku: product.sku,
          quantity: 1,
          product
        }
      ]);
    }
  };

  if (!isMobile || !hasTapbarContainer || isHidden) return null;

  return (
    <UiModPortalBase
      getContainer={() => document.getElementById(TAPBAR_MOBILE_ID)}
    >
      <div
        className={slots.base()}
        data-mobile-app-hidden="commerce"
      >
        {cartCount > 0 && (
          <UiProductCounterInputBase
            onChange={onCartChange}
            className={slots.counter()}
            value={cartCount}
            max={product.stockQuantity}
          />
        )}
        <UiKitButtonBase
          className={slots.addToCart()}
          onClick={onCartClick}
        >
          {product.stockStatus === 'OUT_OF_STOCK' &&
            t('Find out about admission')}
          {product.stockStatus === 'IN_STOCK' && cartCount > 0 && t('In cart')}
          {product.stockStatus === 'IN_STOCK' &&
            cartCount === 0 &&
            t('To cart')}
        </UiKitButtonBase>
        <div className={slots.wishlist()}>
          <UiProductWishlistButton
            className={slots.wishlistIcon()}
            isActive={isInWishlist}
            onClick={onWishlistClick}
          />
        </div>
      </div>
    </UiModPortalBase>
  );
}
